import { useContext, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import CircularProgress from '@mui/material/CircularProgress';
import toast from 'react-hot-toast';
import { AuthContext } from '../auth/AuthContext';
import {
  getPerformanceReviewEmployee,
  patchPerformanceReviewSuperadminVisibility,
  publishPerformanceReview,
  putPerformanceReviewManager,
  putPerformanceReviewSuperAdmin,
  requestPerformanceReviewRevision,
} from '../api/performanceReviewApi';
import {
  canPublishPerformanceReview,
  PERFORMANCE_REVIEW_PUBLISH_TO_EMPLOYEE_UI_ENABLED,
} from './PerformanceReviewCycleSetup';
import { sanitizeSpreadsheetCell, escapeCsvField, downloadCsv } from '../utils/csvExport';

const inputStyle = {
  width: '100%',
  padding: '0.35rem 0.45rem',
  borderRadius: 6,
  border: '1px solid #e2e8f0',
  fontSize: '0.78rem',
  boxSizing: 'border-box',
};

const labelStyle = { display: 'block', fontSize: '0.68rem', fontWeight: 700, color: '#64748b', marginBottom: 2 };

/** How you rate the employee on each objective (distinct from the employee self-rating guide in My Tasks). */
const RATING_HELP_MANAGER =
  '0 = none · 1 = below expectations · 2 = partial · 3 = meets · 4 = exceeds · 5 = extraordinary';

/** # | task | weight | self | mgr rating | mgr comment [ | exec rating | exec comment ] */
const MGR_ROW_GRID_BASE = '36px minmax(120px, 2fr) 64px 52px 88px minmax(110px, 1.5fr)';
const MGR_ROW_GRID_LEADERSHIP = `${MGR_ROW_GRID_BASE} 88px minmax(110px, 1.4fr)`;
/** Leadership view when the reviewee is a manager — executive calibration only (no manager layer). */
const EXEC_ONLY_LEADERSHIP_GRID = '36px minmax(120px, 2fr) 64px 52px 88px minmax(110px, 1.4fr)';
/** Non-leadership viewer without manager-calibration access (objectives + self only). */
const PEER_ROW_GRID = '36px minmax(120px, 2fr) 64px 52px';

// sanitizeSpreadsheetCell / escapeCsvField / downloadCsv now live in
// utils/csvExport.js so the Tracker report shares the exact same escaping.

function buildManagerReviewTsv(sub, localLines, localOverall, name, period, includeExec, includeManagerReview = true) {
  const header = [
    '#',
    'Task / objectives',
    'Weight %',
    'Self rating',
    ...(includeManagerReview ? ['Manager rating', 'Manager comment'] : []),
    ...(includeExec ? ['Executive rating', 'Executive comment'] : []),
  ].join('\t');
  const meta = [
    `Employee\t${sanitizeSpreadsheetCell(name)}`,
    `Period\t${sanitizeSpreadsheetCell(period)}`,
    `Status\t${sanitizeSpreadsheetCell(sub?.status)}`,
    '',
    header,
  ];
  const rows = (localLines || []).map((l, i) => {
    const cells = [i + 1, l.task_description, l.weight_percent, l.self_rating];
    if (includeManagerReview) {
      cells.push(l.manager_rating || '', l.manager_comment || '');
    }
    if (includeExec) {
      cells.push(l.superadmin_rating || '', l.superadmin_comment || '');
    }
    return cells.map(sanitizeSpreadsheetCell).join('\t');
  });
  /** Same shape as CSV: label + value on one row so Sheets/Excel columns A:B align. */
  const tail = [''];
  if (includeManagerReview) {
    tail.push(`Overall manager comment\t${sanitizeSpreadsheetCell(localOverall)}`);
  }
  if (includeExec && sub?.superadmin_overall_comment != null && String(sub.superadmin_overall_comment).trim()) {
    tail.push(`Overall executive comment\t${sanitizeSpreadsheetCell(sub.superadmin_overall_comment)}`);
  }
  tail.push(
    '',
    `Employee achievements / highlights\t${sanitizeSpreadsheetCell(sub?.employee_achievements_text)}`,
    '',
    `Employee support / resources\t${sanitizeSpreadsheetCell(sub?.employee_support_requests_text)}`,
  );
  if (sub?.manager_revision_feedback) {
    tail.push(
      '',
      `Revision feedback (from manager)\t${sanitizeSpreadsheetCell(sub.manager_revision_feedback)}`,
    );
  }
  const body = [...meta, ...rows, ...tail].join('\n');
  return `\ufeff${body}`;
}

function buildManagerReviewCsv(sub, localLines, localOverall, name, period, includeExec, includeManagerReview = true) {
  const row = (cells) => cells.map(escapeCsvField).join(',');
  const headerCells = [
    '#',
    'Task / objectives',
    'Weight %',
    'Self rating',
    ...(includeManagerReview ? ['Manager rating', 'Manager comment'] : []),
    ...(includeExec ? ['Executive rating', 'Executive comment'] : []),
  ];
  const linesOut = [
    row(['Employee', name]),
    row(['Period', period]),
    row(['Status', sub?.status || '']),
    '',
    row(headerCells),
    ...(localLines || []).map((l, i) => {
      const cells = [i + 1, l.task_description, l.weight_percent, l.self_rating];
      if (includeManagerReview) cells.push(l.manager_rating || '', l.manager_comment || '');
      if (includeExec) cells.push(l.superadmin_rating || '', l.superadmin_comment || '');
      return row(cells);
    }),
    '',
    ...(includeManagerReview ? [row(['Overall manager comment', localOverall || ''])] : []),
  ];
  if (includeExec && sub?.superadmin_overall_comment != null && String(sub.superadmin_overall_comment).trim()) {
    linesOut.push('', row(['Overall executive comment', sub.superadmin_overall_comment]));
  }
  linesOut.push(
    '',
    row(['Employee achievements / highlights', sub?.employee_achievements_text || '']),
    '',
    row(['Employee support / resources', sub?.employee_support_requests_text || '']),
  );
  if (sub?.manager_revision_feedback) {
    linesOut.push('', row(['Revision feedback (from manager)', sub.manager_revision_feedback]));
  }
  return linesOut.join('\r\n');
}

async function copyTextToClipboard(text) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const ta = document.createElement('textarea');
  ta.value = text;
  ta.style.position = 'fixed';
  ta.style.left = '-9999px';
  document.body.appendChild(ta);
  ta.select();
  try {
    document.execCommand('copy');
  } finally {
    document.body.removeChild(ta);
  }
}

function parseLineRating(value) {
  if (value === '' || value == null) return null;
  const n = typeof value === 'number' ? value : parseInt(String(value), 10);
  return Number.isFinite(n) ? n : null;
}

function parseLineWeight(weightPercent) {
  const n = Number(weightPercent);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

/** Weighted by objective weight % when weights exist; otherwise simple mean over rows with a rating. */
function averageRatingForLines(lines, ratingField) {
  let weightedSum = 0;
  let weightTotal = 0;
  let simpleSum = 0;
  let simpleCount = 0;
  for (const line of lines) {
    const rating = parseLineRating(line[ratingField]);
    if (rating === null) continue;
    simpleSum += rating;
    simpleCount += 1;
    const w = parseLineWeight(line.weight_percent);
    if (w > 0) {
      weightedSum += rating * w;
      weightTotal += w;
    }
  }
  if (weightTotal > 0) {
    return { avg: weightedSum / weightTotal, usedWeights: true };
  }
  if (simpleCount > 0) {
    return { avg: simpleSum / simpleCount, usedWeights: false };
  }
  return { avg: null, usedWeights: false };
}

function formatRatingAvg(avg) {
  if (avg == null || !Number.isFinite(avg)) return '—';
  const rounded = Math.round(avg * 100) / 100;
  return String(rounded);
}

export default function PerformanceReviewManagerModal({ open, onClose, employeeUserId, employeeName, year, quarter }) {
  const { role, user } = useContext(AuthContext);
  const qc = useQueryClient();
  const [overall, setOverall] = useState('');
  const [saOverall, setSaOverall] = useState('');
  const [lines, setLines] = useState([]);
  const [reeditOpen, setReeditOpen] = useState(false);
  const [reeditComment, setReeditComment] = useState('');

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['performance-review-employee', employeeUserId, year, quarter],
    queryFn: () => getPerformanceReviewEmployee(employeeUserId, { year, quarter }),
    enabled: open && !!employeeUserId && year != null && quarter != null,
  });

  const submission = data?.submission;
  const cycle = data?.cycle;
  const status = submission?.status;
  const viewerId = user?.id ?? user?.userId;
  const isSelfReview =
    viewerId != null &&
    employeeUserId != null &&
    String(viewerId) === String(employeeUserId);
  /** From API: false when viewer is not the reviewee's manager (or admin); never calibrate own review. */
  const canSeeManagerCalibration =
    !isSelfReview && data?.can_see_manager_calibration !== false;
  const r = String(role || '').toLowerCase();
  const showLeadershipCols = r === 'superadmin' || r === 'admin';
  /** Manager-role reviewees are calibrated by executive only (no line manager layer). */
  const executiveOnlyReviewee = ['manager', 'product_manager'].includes(
    String(submission?.employee_role || '').toLowerCase(),
  );
  const showManagerReviewColumns =
    canSeeManagerCalibration && !(showLeadershipCols && executiveOnlyReviewee);
  const canEditManager = status === 'submitted' && !executiveOnlyReviewee && canSeeManagerCalibration;
  const userCanPublishByRole = canPublishPerformanceReview(role);
  const showPublishButton =
    PERFORMANCE_REVIEW_PUBLISH_TO_EMPLOYEE_UI_ENABLED &&
    userCanPublishByRole &&
    status === 'submitted';
  const rowGrid =
    showLeadershipCols && executiveOnlyReviewee
      ? EXEC_ONLY_LEADERSHIP_GRID
      : showLeadershipCols
        ? MGR_ROW_GRID_LEADERSHIP
        : showManagerReviewColumns
          ? MGR_ROW_GRID_BASE
          : PEER_ROW_GRID;
  const canEditSuperadmin = r === 'superadmin' && (status === 'submitted' || status === 'published');
  const canToggleSaVisibility = canPublishPerformanceReview(role) && status === 'published';

  useEffect(() => {
    if (!open || !submission || !submission.lines) {
      if (!open) {
        setOverall('');
        setSaOverall('');
        setLines([]);
        setReeditOpen(false);
        setReeditComment('');
      }
      return;
    }
    setOverall(submission.manager_overall_comment || '');
    setSaOverall(submission.superadmin_overall_comment || '');
    setLines(
      (submission.lines || []).map((l) => ({
        id: l.id,
        task_description: l.task_description,
        weight_percent: l.weight_percent,
        self_rating: l.self_rating,
        manager_rating: l.manager_rating != null ? String(l.manager_rating) : '',
        manager_comment: l.manager_comment || '',
        superadmin_rating: l.superadmin_rating != null ? String(l.superadmin_rating) : '',
        superadmin_comment: l.superadmin_comment || '',
      }))
    );
  }, [open, submission]);

  const saveReviewMutation = useMutation({
    mutationFn: async () => {
      if (!submission?.id) throw new Error('No submission');
      if (!canEditManager && !canEditSuperadmin) throw new Error('Nothing to save');
      if (canEditManager) {
        await putPerformanceReviewManager(submission.id, {
          manager_overall_comment: overall,
          lines: lines.map((l) => ({
            id: l.id,
            manager_rating: l.manager_rating,
            manager_comment: l.manager_comment,
          })),
        });
      }
      if (canEditSuperadmin) {
        await putPerformanceReviewSuperAdmin(submission.id, {
          superadmin_overall_comment: saOverall,
          lines: lines.map((l) => ({
            id: l.id,
            superadmin_rating: l.superadmin_rating,
            superadmin_comment: l.superadmin_comment,
          })),
        });
      }
    },
    onSuccess: () => {
      toast.success('Review saved');
      qc.invalidateQueries({ queryKey: ['performance-review-employee', employeeUserId, year, quarter] });
      refetch();
    },
    onError: (e) => toast.error(e.response?.data?.message || e.message || 'Save failed'),
  });

  const publishMutation = useMutation({
    mutationFn: () => publishPerformanceReview(submission.id),
    onSuccess: () => {
      toast.success('Published');
      qc.invalidateQueries({ queryKey: ['performance-review-employee', employeeUserId, year, quarter] });
      refetch();
    },
    onError: (e) => toast.error(e.response?.data?.message || 'Publish failed'),
  });

  const saVisibilityMutation = useMutation({
    mutationFn: (visible) => patchPerformanceReviewSuperadminVisibility(submission.id, visible),
    onSuccess: (_, visible) => {
      toast.success(visible ? 'Executive feedback visible to employee' : 'Executive feedback hidden from employee');
      qc.invalidateQueries({ queryKey: ['performance-review-employee', employeeUserId, year, quarter] });
      refetch();
    },
    onError: (e) => toast.error(e.response?.data?.message || 'Update failed'),
  });

  const requestRevisionMutation = useMutation({
    mutationFn: () =>
      requestPerformanceReviewRevision(submission.id, { feedback_text: reeditComment.trim() }),
    onSuccess: () => {
      toast.success('Sent back for re-edit');
      setReeditOpen(false);
      setReeditComment('');
      qc.invalidateQueries({ queryKey: ['performance-review-employee', employeeUserId, year, quarter] });
      qc.invalidateQueries({ queryKey: ['performance-review-me'] });
      refetch();
    },
    onError: (e) => toast.error(e.response?.data?.message || 'Request failed'),
  });

  const updateLine = (idx, patch) => {
    setLines((prev) => prev.map((row, i) => (i === idx ? { ...row, ...patch } : row)));
  };

  const ratingAverages = useMemo(() => {
    if (!lines.length) {
      return {
        self: { avg: null, usedWeights: false },
        mgr: { avg: null, usedWeights: false },
        exec: { avg: null, usedWeights: false },
      };
    }
    return {
      self: averageRatingForLines(lines, 'self_rating'),
      mgr: averageRatingForLines(lines, 'manager_rating'),
      exec: averageRatingForLines(lines, 'superadmin_rating'),
    };
  }, [lines]);

  if (!open) return null;

  const cycleLabel = cycle ? cycle.label || `${cycle.year} Q${cycle.quarter}` : '—';
  const titleName = employeeName || `User #${employeeUserId}`;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,23,42,0.55)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2100,
        padding: '10px',
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="pr-manager-modal-title"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff',
          borderRadius: 12,
          width: 'calc(100vw - 20px)',
          maxWidth: 'calc(100vw - 20px)',
          maxHeight: '92vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 25px 50px rgba(0,0,0,0.25)',
        }}
      >
        <div
          style={{
            padding: '0.75rem 1.1rem',
            borderBottom: '1px solid #e2e8f0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexShrink: 0,
          }}
        >
          <div>
            <h2 id="pr-manager-modal-title" style={{ margin: 0, fontSize: '1.08rem', fontWeight: 800, color: '#0f172a' }}>
              Performance review — {titleName}
            </h2>
            <div style={{ fontSize: '0.78rem', color: '#64748b', marginTop: 2 }}>{cycleLabel}</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{ border: 'none', background: 'none', fontSize: '1.35rem', cursor: 'pointer', color: '#94a3b8' }}
          >
            ×
          </button>
        </div>

        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '0.75rem 1.1rem' }}>
          {isLoading ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '2rem', gap: 12 }}>
              <CircularProgress size={36} />
              <span style={{ color: '#64748b', fontSize: '0.9rem' }}>Loading submission…</span>
            </div>
          ) : !cycle ? (
            <p style={{ color: '#64748b', fontSize: '0.9rem' }}>No review cycle for this period.</p>
          ) : !submission || submission.status === 'draft' || !submission.lines ? (
            <p style={{ color: '#64748b', fontSize: '0.9rem' }}>
              This review is not available yet (still in draft or was removed). Close and refresh the profile tab.
            </p>
          ) : (
            <>
              {status === 'published' && (
                <div
                  style={{
                    background: '#f0fdf4',
                    color: '#166534',
                    padding: '0.5rem 0.65rem',
                    borderRadius: 6,
                    fontSize: '0.78rem',
                    fontWeight: 600,
                    marginBottom: '0.65rem',
                  }}
                >
                  Published — read-only. The employee can see their submission plus your feedback.
                </div>
              )}
              {canEditManager && !userCanPublishByRole && (
                <div
                  style={{
                    background: '#EAF6FD',
                    border: '1px solid #a9ddf5',
                    color: '#1e3a5f',
                    padding: '0.5rem 0.65rem',
                    borderRadius: 6,
                    fontSize: '0.78rem',
                    lineHeight: 1.45,
                    marginBottom: '0.65rem',
                  }}
                >
                  <strong>Manager step:</strong> save your ratings and comments here. An <strong>admin or superadmin</strong>{' '}
                  {PERFORMANCE_REVIEW_PUBLISH_TO_EMPLOYEE_UI_ENABLED ? (
                    <>
                      must use <strong>Publish to employee</strong> (same dialog, admin account) before the employee sees
                      manager feedback.
                    </>
                  ) : (
                    <>
                      will publish manager feedback to the employee when that workflow is turned on in the product.
                    </>
                  )}
                </div>
              )}
              {status === 'revision_requested' && submission.manager_revision_feedback && (
                <div
                  style={{
                    background: '#fffbeb',
                    border: '1px solid #fcd34d',
                    color: '#92400e',
                    padding: '0.5rem 0.65rem',
                    borderRadius: 6,
                    fontSize: '0.78rem',
                    marginBottom: '0.65rem',
                    lineHeight: 1.45,
                  }}
                >
                  <span style={{ fontWeight: 800 }}>Revision requested — </span>
                  <span style={{ whiteSpace: 'pre-wrap' }}>{submission.manager_revision_feedback}</span>
                </div>
              )}
              {!canSeeManagerCalibration && !showLeadershipCols && submission?.lines?.length > 0 ? (
                <div
                  style={{
                    background: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    color: '#475569',
                    padding: '0.5rem 0.65rem',
                    borderRadius: 6,
                    fontSize: '0.78rem',
                    lineHeight: 1.45,
                    marginBottom: '0.65rem',
                  }}
                >
                  <strong>Visibility:</strong> Manager ratings and comments are visible only to this person’s assigned
                  manager and to admin or superadmin. You can review objectives and self-ratings here.
                </div>
              ) : null}

              {(submission.employee_achievements_text || submission.employee_support_requests_text) && (
                <div style={{ display: 'grid', gap: 10, marginBottom: '0.85rem' }}>
                  {submission.employee_achievements_text ? (
                    <div>
                      <div style={labelStyle}>Employee — achievements / highlights</div>
                      <div
                        style={{
                          ...inputStyle,
                          background: '#f8fafc',
                          minHeight: 56,
                          lineHeight: 1.45,
                          whiteSpace: 'pre-wrap',
                        }}
                      >
                        {submission.employee_achievements_text}
                      </div>
                    </div>
                  ) : null}
                  {submission.employee_support_requests_text ? (
                    <div>
                      <div style={labelStyle}>Employee — support / what would help</div>
                      <div
                        style={{
                          ...inputStyle,
                          background: '#f8fafc',
                          minHeight: 48,
                          lineHeight: 1.45,
                          whiteSpace: 'pre-wrap',
                        }}
                      >
                        {submission.employee_support_requests_text}
                      </div>
                    </div>
                  ) : null}
                </div>
              )}

              <div style={{ overflowX: 'auto', marginBottom: 8 }}>
                <div
                  style={{
                    minWidth: showLeadershipCols
                      ? executiveOnlyReviewee
                        ? 720
                        : 980
                      : showManagerReviewColumns
                        ? 720
                        : 520,
                  }}
                >
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: rowGrid,
                      gap: '0.35rem 0.45rem',
                      alignItems: 'center',
                      padding: '0.25rem 0.35rem 0.5rem',
                      fontSize: '0.65rem',
                      fontWeight: 700,
                      color: '#64748b',
                      borderBottom: '1px solid #e2e8f0',
                      marginBottom: 6,
                    }}
                  >
                    <span style={{ textAlign: 'center' }}>#</span>
                    <span>Task / objectives</span>
                    <span>Wt %</span>
                    <span>Self</span>
                    {showManagerReviewColumns ? (
                      <>
                        <span>Mgr rating</span>
                        <span>Mgr comment</span>
                      </>
                    ) : null}
                    {showLeadershipCols ? (
                      <>
                        <span>Exec rating</span>
                        <span>Exec comment</span>
                      </>
                    ) : null}
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.45rem' }}>
                    {lines.map((line, idx) => (
                      <div
                        key={line.id}
                        style={{
                          display: 'grid',
                          gridTemplateColumns: rowGrid,
                          gap: '0.35rem 0.45rem',
                          alignItems: 'stretch',
                          padding: '0.4rem 0.35rem',
                          borderRadius: 8,
                          background: '#fafafa',
                          border: '1px solid #eef2f7',
                        }}
                      >
                        <span
                          style={{
                            alignSelf: 'start',
                            textAlign: 'center',
                            fontSize: '0.75rem',
                            fontWeight: 700,
                            color: '#94a3b8',
                            paddingTop: 6,
                          }}
                        >
                          {idx + 1}
                        </span>
                        <span
                          style={{
                            alignSelf: 'start',
                            fontSize: '0.78rem',
                            color: '#334155',
                            lineHeight: 1.35,
                            paddingTop: 4,
                            whiteSpace: 'pre-wrap',
                          }}
                        >
                          {line.task_description}
                        </span>
                        <span
                          style={{
                            alignSelf: 'start',
                            fontSize: '0.78rem',
                            color: '#64748b',
                            paddingTop: 6,
                          }}
                        >
                          {line.weight_percent}
                        </span>
                        <span
                          style={{
                            alignSelf: 'start',
                            fontSize: '0.78rem',
                            fontWeight: 700,
                            color: '#475569',
                            paddingTop: 6,
                          }}
                        >
                          {line.self_rating}
                        </span>
                        {showManagerReviewColumns ? (
                          <>
                            <select
                              style={{
                                ...inputStyle,
                                alignSelf: 'start',
                                background: canEditManager ? '#fff7ed' : '#f8fafc',
                                marginTop: 2,
                              }}
                              disabled={!canEditManager}
                              value={line.manager_rating}
                              onChange={(e) => updateLine(idx, { manager_rating: e.target.value })}
                            >
                              <option value="">—</option>
                              {[0, 1, 2, 3, 4, 5].map((n) => (
                                <option key={n} value={n}>
                                  {n}
                                </option>
                              ))}
                            </select>
                            <textarea
                              rows={1}
                              style={{
                                ...inputStyle,
                                minHeight: 52,
                                height: '100%',
                                lineHeight: 1.35,
                                background: canEditManager ? '#fff7ed' : '#f8fafc',
                                resize: 'vertical',
                              }}
                              disabled={!canEditManager}
                              value={line.manager_comment}
                              onChange={(e) => updateLine(idx, { manager_comment: e.target.value })}
                              placeholder={canEditManager ? 'Feedback on this objective…' : ''}
                            />
                          </>
                        ) : null}
                        {showLeadershipCols ? (
                          <>
                            <select
                              style={{
                                ...inputStyle,
                                alignSelf: 'start',
                                background: canEditSuperadmin ? '#f5f3ff' : '#f8fafc',
                                marginTop: 2,
                              }}
                              disabled={!canEditSuperadmin}
                              value={line.superadmin_rating}
                              onChange={(e) => updateLine(idx, { superadmin_rating: e.target.value })}
                            >
                              <option value="">—</option>
                              {[0, 1, 2, 3, 4, 5].map((n) => (
                                <option key={n} value={n}>
                                  {n}
                                </option>
                              ))}
                            </select>
                            <textarea
                              rows={1}
                              style={{
                                ...inputStyle,
                                minHeight: 52,
                                height: '100%',
                                lineHeight: 1.35,
                                background: canEditSuperadmin ? '#f5f3ff' : '#f8fafc',
                                resize: 'vertical',
                              }}
                              disabled={!canEditSuperadmin}
                              value={line.superadmin_comment}
                              onChange={(e) => updateLine(idx, { superadmin_comment: e.target.value })}
                              placeholder={canEditSuperadmin ? 'Executive note…' : ''}
                            />
                          </>
                        ) : null}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {showManagerReviewColumns ? (
                <div style={{ marginBottom: '0.75rem' }}>
                  <label style={labelStyle}>Overall manager comment</label>
                  <textarea
                    style={{ ...inputStyle, minHeight: 80, background: canEditManager ? '#fff7ed' : '#f8fafc' }}
                    disabled={!canEditManager}
                    value={overall}
                    onChange={(e) => setOverall(e.target.value)}
                  />
                </div>
              ) : null}

              {showLeadershipCols ? (
                <div style={{ marginBottom: '0.75rem' }}>
                  <label style={labelStyle}>Overall executive (superadmin) comment</label>
                  <textarea
                    style={{ ...inputStyle, minHeight: 72, background: canEditSuperadmin ? '#f5f3ff' : '#f8fafc' }}
                    disabled={!canEditSuperadmin}
                    value={saOverall}
                    onChange={(e) => setSaOverall(e.target.value)}
                    placeholder={canEditSuperadmin ? 'Optional summary for leadership calibration…' : ''}
                  />
                  {canToggleSaVisibility ? (
                    <div
                      style={{
                        marginTop: 12,
                        padding: '0.55rem 0.65rem',
                        borderRadius: 8,
                        background: submission?.superadmin_visible_to_employee ? '#ecfdf5' : '#fef3c7',
                        border: `1px solid ${submission?.superadmin_visible_to_employee ? '#6ee7b7' : '#fcd34d'}`,
                        fontSize: '0.75rem',
                        color: '#422006',
                        lineHeight: 1.45,
                      }}
                    >
                      <strong>Employee visibility (executive layer):</strong>{' '}
                      {submission?.superadmin_visible_to_employee
                        ? 'On — the employee sees executive ratings/comments in their portal.'
                        : executiveOnlyReviewee
                          ? 'Off by default — executive calibration stays internal until you share it with the employee.'
                          : 'Off by default — manager feedback is published; executive layer stays internal until you turn this on.'}
                      <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                        <button
                          type="button"
                          disabled={saVisibilityMutation.isPending || submission?.superadmin_visible_to_employee}
                          onClick={() => saVisibilityMutation.mutate(true)}
                          style={{
                            padding: '0.35rem 0.75rem',
                            borderRadius: 6,
                            border: 'none',
                            background: '#059669',
                            color: '#fff',
                            fontWeight: 700,
                            fontSize: '0.75rem',
                            cursor: submission?.superadmin_visible_to_employee ? 'not-allowed' : 'pointer',
                            opacity: submission?.superadmin_visible_to_employee ? 0.5 : 1,
                          }}
                        >
                          Share executive feedback with employee
                        </button>
                        <button
                          type="button"
                          disabled={saVisibilityMutation.isPending || !submission?.superadmin_visible_to_employee}
                          onClick={() => saVisibilityMutation.mutate(false)}
                          style={{
                            padding: '0.35rem 0.75rem',
                            borderRadius: 6,
                            border: '1px solid #cbd5e1',
                            background: '#fff',
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            cursor: !submission?.superadmin_visible_to_employee ? 'not-allowed' : 'pointer',
                            opacity: !submission?.superadmin_visible_to_employee ? 0.5 : 1,
                          }}
                        >
                          Hide executive feedback from employee
                        </button>
                      </div>
                    </div>
                  ) : null}
                </div>
              ) : null}

              {lines.length > 0 ? (
                <div
                  style={{
                    marginBottom: '0.75rem',
                    padding: '0.55rem 0.65rem',
                    borderRadius: 8,
                    background: '#f1f5f9',
                    border: '1px solid #e2e8f0',
                  }}
                >
                  <div
                    style={{
                      fontSize: '0.72rem',
                      fontWeight: 800,
                      color: '#334155',
                      marginBottom: 6,
                    }}
                  >
                    Average rating (0–5)
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '0.65rem 1.25rem',
                      fontSize: '0.78rem',
                      color: '#475569',
                      alignItems: 'baseline',
                    }}
                  >
                    <span title="Employee self-rating on each objective">
                      <span style={{ fontWeight: 700, color: '#64748b' }}>Self: </span>
                      <strong style={{ color: '#0f172a' }}>{formatRatingAvg(ratingAverages.self.avg)}</strong>
                      {ratingAverages.self.usedWeights ? (
                        <span style={{ fontSize: '0.68rem', color: '#94a3b8', marginLeft: 4 }}>(weighted)</span>
                      ) : null}
                    </span>
                    {showManagerReviewColumns ? (
                      <span title="Manager rating per objective">
                        <span style={{ fontWeight: 700, color: '#9a3412' }}>Manager: </span>
                        <strong style={{ color: '#0f172a' }}>{formatRatingAvg(ratingAverages.mgr.avg)}</strong>
                        {ratingAverages.mgr.usedWeights ? (
                          <span style={{ fontSize: '0.68rem', color: '#94a3b8', marginLeft: 4 }}>(weighted)</span>
                        ) : null}
                      </span>
                    ) : null}
                    {showLeadershipCols ? (
                      <span title="Executive (superadmin) calibration rating">
                        <span style={{ fontWeight: 700, color: '#5b21b6' }}>Executive: </span>
                        <strong style={{ color: '#0f172a' }}>{formatRatingAvg(ratingAverages.exec.avg)}</strong>
                        {ratingAverages.exec.usedWeights ? (
                          <span style={{ fontSize: '0.68rem', color: '#94a3b8', marginLeft: 4 }}>(weighted)</span>
                        ) : null}
                      </span>
                    ) : null}
                  </div>
                </div>
              ) : null}

              {showManagerReviewColumns ? (
                <div
                  style={{
                    padding: '0.45rem 0.55rem',
                    borderRadius: 6,
                    background: '#f8fafc',
                    fontSize: '0.7rem',
                    color: '#64748b',
                    lineHeight: 1.45,
                    marginBottom: canEditManager || canEditSuperadmin ? 12 : 0,
                  }}
                >
                  <span style={{ fontWeight: 700, color: '#475569' }}>Manager rating guide: </span>
                  {RATING_HELP_MANAGER}
                </div>
              ) : showLeadershipCols ? (
                <div
                  style={{
                    padding: '0.45rem 0.55rem',
                    borderRadius: 6,
                    background: '#f5f3ff',
                    fontSize: '0.7rem',
                    color: '#64748b',
                    lineHeight: 1.45,
                    marginBottom: canEditSuperadmin ? 12 : 0,
                  }}
                >
                  <span style={{ fontWeight: 700, color: '#5b21b6' }}>Executive rating guide: </span>
                  {RATING_HELP_MANAGER}
                </div>
              ) : null}

              {submission && lines.length > 0 && (
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 10,
                    rowGap: 10,
                    paddingBottom: 8,
                  }}
                >
                  <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748b' }}>Export</span>
                    <button
                      type="button"
                      title="Tab-separated — paste into a blank sheet (columns align automatically)"
                      onClick={async () => {
                        try {
                          const tsv = buildManagerReviewTsv(
                            submission,
                            lines,
                            overall,
                            titleName,
                            cycleLabel,
                            showLeadershipCols,
                            showManagerReviewColumns,
                          );
                          await copyTextToClipboard(tsv);
                          toast.success('Copied — paste into Excel or Google Sheets');
                        } catch {
                          toast.error('Could not copy');
                        }
                      }}
                      style={{
                        padding: '0.4rem 0.75rem',
                        borderRadius: 8,
                        border: '1px solid #cbd5e1',
                        background: '#fff',
                        color: '#334155',
                        fontWeight: 700,
                        fontSize: '0.78rem',
                        cursor: 'pointer',
                      }}
                    >
                      Copy for Excel
                    </button>
                    <button
                      type="button"
                      title="UTF-8 CSV with BOM — open directly in Excel"
                      onClick={() => {
                        const csv = buildManagerReviewCsv(
                          submission,
                          lines,
                          overall,
                          titleName,
                          cycleLabel,
                          showLeadershipCols,
                          showManagerReviewColumns,
                        );
                        const safe = String(titleName).replace(/[^\w\d.-]+/g, '_').slice(0, 48);
                        downloadCsv(`performance-review-${safe}-${year}-Q${quarter}.csv`, csv);
                        toast.success('CSV downloaded');
                      }}
                      style={{
                        padding: '0.4rem 0.75rem',
                        borderRadius: 8,
                        border: '1px solid #7CCAF0',
                        background: '#EAF6FD',
                        color: '#2C63AE',
                        fontWeight: 700,
                        fontSize: '0.78rem',
                        cursor: 'pointer',
                      }}
                    >
                      Download CSV
                    </button>
                  </div>
                  {(canEditManager || canEditSuperadmin) && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
                      {canEditManager ? (
                        <button
                          type="button"
                          onClick={() => setReeditOpen(true)}
                          style={{
                            padding: '0.45rem 0.9rem',
                            borderRadius: 8,
                            border: '1px solid #cbd5e1',
                            background: '#fff',
                            color: '#334155',
                            fontWeight: 700,
                            fontSize: '0.82rem',
                            cursor: 'pointer',
                          }}
                        >
                          Ask for re-edit
                        </button>
                      ) : null}
                      {/*
                        Visual order (LTR): grey Publish, then green Save.
                        row-reverse + [Save, Publish] in DOM makes that explicit for all flex layouts.
                      */}
                      <div
                        style={{
                          display: 'inline-flex',
                          flexDirection: 'row-reverse',
                          flexWrap: 'wrap',
                          gap: 10,
                          alignItems: 'center',
                        }}
                      >
                        <button
                          type="button"
                          onClick={() => saveReviewMutation.mutate()}
                          disabled={saveReviewMutation.isPending}
                          style={{
                            padding: '0.45rem 0.9rem',
                            borderRadius: 8,
                            border: 'none',
                            background: '#15803d',
                            color: '#fff',
                            fontWeight: 700,
                            fontSize: '0.82rem',
                            cursor: saveReviewMutation.isPending ? 'wait' : 'pointer',
                          }}
                        >
                          {saveReviewMutation.isPending ? 'Saving…' : 'Save Review'}
                        </button>
                        {showPublishButton ? (
                          <button
                            type="button"
                            onClick={() => {
                              const publishMsg = executiveOnlyReviewee
                                ? 'Publish this review to the employee? They will see their submission plus published feedback (executive calibration when you choose to share it).'
                                : 'Publish this review to the employee? They will be able to see manager ratings and comments.';
                              if (!window.confirm(publishMsg)) {
                                return;
                              }
                              publishMutation.mutate();
                            }}
                            disabled={
                              publishMutation.isPending ||
                              saveReviewMutation.isPending ||
                              lines.some((l) => {
                                if (executiveOnlyReviewee) {
                                  const sr = parseInt(l.superadmin_rating, 10);
                                  return !Number.isFinite(sr) || sr < 0 || sr > 5;
                                }
                                const mr = parseInt(l.manager_rating, 10);
                                return !Number.isFinite(mr) || mr < 0 || mr > 5;
                              })
                            }
                            style={{
                              padding: '0.45rem 0.9rem',
                              borderRadius: 8,
                              border: 'none',
                              background: '#475569',
                              color: '#fff',
                              fontWeight: 700,
                              fontSize: '0.82rem',
                              cursor: publishMutation.isPending ? 'wait' : 'pointer',
                            }}
                          >
                            {publishMutation.isPending ? 'Publishing…' : 'Publish to employee'}
                          </button>
                        ) : null}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {reeditOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 2200,
            background: 'rgba(15,23,42,0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Ask for re-edit"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#fff',
              borderRadius: 10,
              padding: '1rem 1.1rem',
              maxWidth: 440,
              width: '100%',
              boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
            }}
          >
            <div style={{ fontWeight: 800, marginBottom: 6, color: '#0f172a', fontSize: '1rem' }}>Ask for re-edit</div>
            <p style={{ fontSize: '0.8rem', color: '#64748b', margin: '0 0 10px', lineHeight: 1.45 }}>
              Shown to the employee on My Tasks so they can update and resubmit.
            </p>
            <label style={labelStyle}>Comment</label>
            <textarea
              rows={4}
              value={reeditComment}
              onChange={(e) => setReeditComment(e.target.value)}
              style={{ ...inputStyle, width: '100%', minHeight: 96, marginBottom: 12, resize: 'vertical' }}
              placeholder="What should they change?"
            />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={() => setReeditOpen(false)}
                style={{
                  padding: '0.45rem 0.85rem',
                  borderRadius: 8,
                  border: '1px solid #e2e8f0',
                  background: '#fff',
                  fontWeight: 600,
                  fontSize: '0.82rem',
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={!reeditComment.trim() || requestRevisionMutation.isPending}
                onClick={() => requestRevisionMutation.mutate()}
                style={{
                  padding: '0.45rem 0.85rem',
                  borderRadius: 8,
                  border: 'none',
                  background: '#1272B3',
                  color: '#fff',
                  fontWeight: 700,
                  fontSize: '0.82rem',
                  cursor: !reeditComment.trim() || requestRevisionMutation.isPending ? 'not-allowed' : 'pointer',
                  opacity: !reeditComment.trim() || requestRevisionMutation.isPending ? 0.65 : 1,
                }}
              >
                {requestRevisionMutation.isPending ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
